import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calculator, Upload, FileText, Search, Trash2, Download, Plus, Database, CheckCircle, AlertTriangle } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/admin';
import { Button, Badge, Card, Spinner } from '@/components/common';
import { DataTable } from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';
import { gaeb } from '@/utils/api';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGaebProjects, useDatanormStatus } from '@/hooks/useGaeb';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'info' },
  processing: { label: 'Wird verarbeitet', color: 'warning' },
  matched: { label: 'Abgeglichen', color: 'success' },
  exported: { label: 'Exportiert', color: 'gray' },
  error: { label: 'Fehler', color: 'danger' },
};

const PHASE_LABELS = {
  clearing: 'Datenbank wird geleert...',
  counting: 'Dateien werden gezählt...',
  parsing_datanorm: 'DATANORM-Dateien werden eingelesen...',
  parsing_datpreis: 'DATPREIS-Dateien werden eingelesen...',
  inserting: 'Artikel werden gespeichert...',
  done: 'Import abgeschlossen',
};

const Kalkulation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Project list state
  const [searchTerm, setSearchTerm] = useState('');

  // Upload state
  const [projectName, setProjectName] = useState('');
  const [lvFile, setLvFile] = useState(null);
  const lvInputRef = useRef(null);

  // Datanorm state
  const [importing, setImporting] = useState(false);
  const [importPhase, setImportPhase] = useState('');
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importSummary, setImportSummary] = useState(null);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);

  const { data: projects = [], isLoading: loading } = useGaebProjects();
  const { data: datanormStatus, isLoading: datanormLoading } = useDatanormStatus();

  const createProjectMutation = useMutation({
    mutationFn: (formData: FormData) => gaeb.createProject(formData),
    onSuccess: (result) => {
      toast.success(`Projekt angelegt — ${result.data?.totalPositions || 0} Positionen`);
      setProjectName('');
      setLvFile(null);
      if (lvInputRef.current) lvInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['gaeb'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Fehler beim Anlegen des Projekts');
      console.error(err);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: (id: number) => gaeb.deleteProject(id),
    onSuccess: () => {
      setDeleteId(null);
      toast.success('Projekt gelöscht');
      queryClient.invalidateQueries({ queryKey: ['gaeb'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Fehler beim Löschen');
      console.error(err);
    },
  });

  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!projectName.trim()) {
      toast.error('Projektname ist erforderlich');
      return;
    }
    if (!lvFile) {
      toast.error('LV-Datei ist erforderlich');
      return;
    }

    const formData = new FormData();
    formData.append('name', projectName.trim());
    // Alles geht in den p93-Slot – Backend erkennt das Format automatisch (P93/D83/X83/X81)
    formData.append('p93', lvFile);
    createProjectMutation.mutate(formData);
  };

  const handleDelete = (id: number) => {
    deleteProjectMutation.mutate(id);
  };

  const handleDatanormImport = async () => {
    setImporting(true);
    setImportPhase('');
    setImportProgress(0);
    setImportTotal(0);
    setImportSummary(null);

    try {
      const response = await fetch('/api/gaeb/datanorm/import', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;

          try {
            const jsonStr = trimmed.slice(5).trim();
            if (!jsonStr) continue;
            const event = JSON.parse(jsonStr);

            if (event.phase) {
              setImportPhase(event.phase);
            }
            if (event.current != null) {
              setImportProgress(event.current);
            }
            if (event.total != null) {
              setImportTotal(event.total);
            }
            if (event.phase === 'done') {
              setImportSummary({
                articlesImported: event.articlesImported || event.total || 0,
                duration: event.duration || 0,
              });
            }
            if (event.error) {
              toast.error(`Import-Fehler: ${event.error}`);
              setImporting(false);
              return;
            }
          } catch {
            // Skip unparseable lines
          }
        }
      }

      toast.success('Datanorm-Import abgeschlossen');
      queryClient.invalidateQueries({ queryKey: ['gaeb', 'datanorm', 'status'] });
    } catch (err) {
      toast.error('Fehler beim Datanorm-Import');
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  // Filter projects by search
  const filteredProjects = projects.filter((p) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.name?.toLowerCase().includes(search) ||
      p.projectRef?.toLowerCase().includes(search)
    );
  });

  const renderStatusBar = (project) => {
    const total = project.totalPositions || 0;
    if (total === 0) return <span className="text-sm text-gray-400">-</span>;

    const matched = project.matchedPositions || 0;
    const manual = project.manualPositions || 0;
    const skipped = project.skippedPositions || 0;
    const unmatched = project.unmatchedPositions || 0;

    const pctMatched = (matched / total) * 100;
    const pctManual = (manual / total) * 100;
    const pctUnmatched = (unmatched / total) * 100;
    const pctSkipped = (skipped / total) * 100;

    return (
      <div className="w-full">
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
          {pctMatched > 0 && (
            <div
              className="bg-blue-500 transition-all"
              style={{ width: `${pctMatched}%` }}
              title={`Artikel zugewiesen: ${matched}`}
            />
          )}
          {pctManual > 0 && (
            <div
              className="bg-blue-400 transition-all"
              style={{ width: `${pctManual}%` }}
              title={`Manuell zugewiesen: ${manual}`}
            />
          )}
          {pctUnmatched > 0 && (
            <div
              className="bg-yellow-500 transition-all"
              style={{ width: `${pctUnmatched}%` }}
              title={`Offen: ${unmatched}`}
            />
          )}
          {pctSkipped > 0 && (
            <div
              className="bg-gray-300 transition-all"
              style={{ width: `${pctSkipped}%` }}
              title={`Übersprungen: ${skipped}`}
            />
          )}
        </div>
        <div className="flex gap-3 mt-1 text-xs text-gray-500">
          {matched > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
              {matched}
            </span>
          )}
          {manual > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
              {manual}
            </span>
          )}
          {unmatched > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
              {unmatched}
            </span>
          )}
          {skipped > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />
              {skipped}
            </span>
          )}
        </div>
      </div>
    );
  };

  const importProgressPercent = importTotal > 0 ? Math.round((importProgress / importTotal) * 100) : 0;

  return (
    <div>
      <PageHeader
        title="GAEB Kalkulation"
        description="Leistungsverzeichnisse importieren, kalkulieren und exportieren"
        actions={
          <Button variant="primary" icon={Calculator} onClick={() => document.getElementById('upload-section')?.scrollIntoView({ behavior: 'smooth' })}>
            Neues Projekt
          </Button>
        }
      />

      {/* Upload + Datanorm Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Upload Section */}
        <Card id="upload-section">
          <Card.Header>
            <Card.Title>
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary-500" />
                Projekt anlegen
              </div>
            </Card.Title>
            <Card.Description>GAEB-Leistungsverzeichnis hochladen und neues Kalkulationsprojekt erstellen</Card.Description>
          </Card.Header>
          <Card.Content>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Projektname *
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="z.B. BV Musterstraße 12 - LV Elektro"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GAEB-Datei (P93, P83, D83, X83) *
                </label>
                <input
                  ref={lvInputRef}
                  type="file"
                  accept=".p93,.P93,.p83,.P83,.d83,.D83,.x83,.X83,.x81,.X81"
                  onChange={(e) => setLvFile(e.target.files[0] || null)}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  required
                />
                <p className="mt-1 text-xs text-gray-400">Leistungsverzeichnis hochladen — alle gängigen GAEB-Formate werden automatisch erkannt</p>
              </div>

              <Button
                type="submit"
                variant="primary"
                icon={Plus}
                loading={createProjectMutation.isPending}
                className="w-full"
              >
                Projekt anlegen
              </Button>
            </form>
          </Card.Content>
        </Card>

        {/* Datanorm Status Card */}
        <Card>
          <Card.Header>
            <Card.Title>
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary-500" />
                Datanorm-Katalog
              </div>
            </Card.Title>
            <Card.Description>Artikeldatenbank für automatische Preiszuordnung</Card.Description>
          </Card.Header>
          <Card.Content>
            {datanormLoading && !datanormStatus ? (
              <Spinner.Center />
            ) : datanormStatus ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {(datanormStatus.totalArticles || 0).toLocaleString('de-DE')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Artikel gesamt</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {(datanormStatus.withNetPrice || 0).toLocaleString('de-DE')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Mit Nettopreis</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {(datanormStatus.withEan || 0).toLocaleString('de-DE')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Mit EAN</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {(datanormStatus.withMetal || 0).toLocaleString('de-DE')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Mit Metallzuschlag</p>
                  </div>
                </div>

                {/* Import Progress */}
                {importing && (
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-blue-700">
                        {PHASE_LABELS[importPhase] || importPhase}
                      </span>
                      {importTotal > 0 && (
                        <span className="text-blue-600">
                          {importProgress.toLocaleString('de-DE')} / {importTotal.toLocaleString('de-DE')}
                        </span>
                      )}
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${importTotal > 0 ? importProgressPercent : 100}%` }}
                      />
                    </div>
                    {importTotal > 0 && (
                      <p className="text-xs text-blue-600 text-right">{importProgressPercent}%</p>
                    )}
                  </div>
                )}

                {/* Import Summary */}
                {importSummary && !importing && (
                  <div className="bg-green-50 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Import abgeschlossen</p>
                      <p className="text-sm text-green-700 mt-1">
                        {importSummary.articlesImported.toLocaleString('de-DE')} Artikel importiert
                        {importSummary.duration > 0 && (
                          <span> in {Math.round(importSummary.duration / 1000)}s</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  variant="primary"
                  icon={Download}
                  onClick={handleDatanormImport}
                  loading={importing}
                  className="w-full"
                >
                  Datanorm importieren
                </Button>
                <Link to="/admin/kalkulation/datanorm" className="block">
                  <Button variant="outline" icon={Search} className="w-full">
                    Katalog durchsuchen
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Datanorm-Status konnte nicht geladen werden</p>
                <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ['gaeb', 'datanorm', 'status'] })} className="mt-2">
                  Erneut versuchen
                </Button>
              </div>
            )}
          </Card.Content>
        </Card>
      </div>

      {/* Search Bar */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Suche..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="text-sm text-gray-500">
            {filteredProjects.length} {filteredProjects.length === 1 ? 'Projekt' : 'Projekte'}
          </div>
        </div>
      </Card>

      {/* Projects Table */}
      {loading ? <div className="space-y-2">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-14 w-full" />)}</div> : (
        <DataTable
          columns={[
            { accessorKey: 'name', header: 'Projekt', cell: ({ row }) => <div><div className="font-medium text-gray-900">{row.original.name}</div>{row.original.projectRef && <div className="text-xs text-gray-500 mt-0.5">{row.original.projectRef}</div>}</div> },
            { accessorKey: 'createdAt', header: 'Erstellt am', cell: ({ row }) => <span className="text-sm text-gray-600">{formatDate(row.original.createdAt)}</span> },
            { accessorFn: (row: any) => row.totalPositions ?? 0, id: 'positions', header: 'Positionen', cell: ({ row }) => <span className="font-medium">{row.original.totalPositions || 0}</span> },
            { id: 'progress', header: 'Fortschritt', enableSorting: false, cell: ({ row }) => renderStatusBar(row.original) },
            { accessorFn: (row: any) => row.totalNetPrice ?? 0, id: 'sum', header: 'Summe', cell: ({ row }) => <span className="font-medium text-gray-900">{formatCurrency(row.original.totalNetPrice)}</span> },
            { accessorKey: 'status', header: 'Status', cell: ({ row }) => { const sc = STATUS_CONFIG[row.original.status] || STATUS_CONFIG.new; return <Badge variant={sc.color}>{sc.label}</Badge> } },
            { id: 'actions', header: 'Aktionen', enableSorting: false, cell: ({ row }) => {
              const project = row.original;
              return <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <Link to={`/admin/kalkulation/${project.id}`}><Button variant="primary" size="sm" icon={FileText}>Öffnen</Button></Link>
                {deleteId === project.id ? (
                  <div className="flex gap-1"><Button variant="danger" size="sm" onClick={() => handleDelete(project.id)}>Ja</Button><Button variant="outline" size="sm" onClick={() => setDeleteId(null)}>Nein</Button></div>
                ) : <Button variant="ghost" size="sm" icon={Trash2} onClick={() => setDeleteId(project.id)} />}
              </div>;
            }},
          ] as ColumnDef<any>[]}
          data={filteredProjects}
          emptyText={searchTerm ? 'Keine Projekte gefunden' : 'Noch keine Kalkulationsprojekte vorhanden'}
        />
      )}

      {/* Legend */}
      {filteredProjects.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
            Zugeordnet (automatisch)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />
            Manuell bepreist
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-yellow-500 inline-block" />
            Offen
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-gray-300 inline-block" />
            Übersprungen
          </span>
        </div>
      )}
    </div>
  );
};

export default Kalkulation;
